import {
  ReactElement, useEffect, useState,
} from 'react';
import { colors } from 'styles/remoteControlColors';
import {
  disabledStyleRoundedButton,
  PowerButton, SetpointButton, SetpointControllerContent, SetpointLabel,
} from '../styles';
import { MinusRCIcon, PlusRCIcon, PowerRCIcon } from '~/icons';
import { useCalculateStrokeSetpoint } from '../hooks/useCalculateStrokeSetpoint';
import Skeleton from 'react-loading-skeleton';

interface SetpointControllerProps {
  readonly limits: {
    tMin: number;
    tMax: number;
  };
  readonly power: boolean | undefined;
  readonly currentSetpoint: number | undefined;
  readonly onClickPower: () => void;
  readonly onChangeSetpoint: (setpoint: number | undefined) => void;
  readonly disabled?: boolean;
  readonly loading?: boolean;
}

interface SetpointVisualizerProps {
  readonly limits: {
    tMin: number;
    tMax: number;
  };
  readonly currentSetpoint: number | undefined;
  readonly disabled?: boolean;
}

function SetpointVisualizer({
  currentSetpoint,
  limits,
  disabled,
}: SetpointVisualizerProps): ReactElement {
  const { setpointStrokeDashArray } = useCalculateStrokeSetpoint(currentSetpoint, limits);

  return (
    <svg width="224" height="210" viewBox="0 0 224 210" style={{ position: 'absolute' }}>
      {/* <!-- Detalhe contorno do círculo --> */}
      <circle
        cx="112"
        cy="105"
        r="98"
        fill="none"
        stroke={colors.Grey300}
        strokeWidth="1"
        strokeDasharray="520 95.4"
        transform="rotate(119 112 105)"
        strokeLinecap="round"
      />

      {/* <!-- Fundo do círculo --> */}
      <circle
        cx="112"
        cy="105"
        r="80"
        fill="none"
        stroke={colors.Grey300}
        strokeWidth="8"
        strokeDasharray="401.63 130.77"
        transform="rotate(126 112 105)"
        strokeLinecap="round"
      />

      {/* <!-- Círculo de progresso --> */}
      {!!currentSetpoint && (
        <circle
          cx="112"
          cy="105"
          r="80"
          fill="none"
          stroke={disabled ? colors.Grey500 : colors.Blue700}
          strokeWidth="8"
          strokeDasharray={setpointStrokeDashArray}
          transform="rotate(126 112 105)"
          strokeLinecap="round"
          style={{
            transition: 'stroke-dasharray 100ms linear',
          }}
        />
      )}
    </svg>
  );
}

export function SetpointController({
  currentSetpoint,
  power,
  limits,
  disabled,
  onClickPower,
  onChangeSetpoint,
  loading,
}: SetpointControllerProps): ReactElement {
  const [setpoint, setSetpoint] = useState(currentSetpoint);

  const minSetpoint = limits.tMin;
  const maxSetpoint = limits.tMax;

  const disabledButtons = !power || disabled || !setpoint;

  function isSetpointOutRange(limit: 'min' | 'max'): boolean {
    if (!setpoint) return false;

    return limit === 'max' ? setpoint > limits.tMax : setpoint < limits.tMin;
  }

  function onClickDecreaseSetpoint(): void {
    if (!setpoint) return;

    if (setpoint - 1 >= limits.tMin) {
      setSetpoint((previousSetpoint) => {
        const newSetpoint = (previousSetpoint ? previousSetpoint - 1 : previousSetpoint);

        onChangeSetpoint(newSetpoint);
        return newSetpoint;
      });
    }
  }

  function onClickIncreaseSetpoint(): void {
    if (!setpoint) return;

    if (isSetpointOutRange('min')) {
      setSetpoint(minSetpoint);
      onChangeSetpoint(minSetpoint);
      return;
    }

    if (isSetpointOutRange('max')) {
      setSetpoint(maxSetpoint);
      onChangeSetpoint(maxSetpoint);
      return;
    }

    if (setpoint + 1 <= limits.tMax) {
      setSetpoint((previousSetpoint) => {
        const newSetpoint = (previousSetpoint ? previousSetpoint + 1 : previousSetpoint);

        onChangeSetpoint(newSetpoint);
        return newSetpoint;
      });
    }
  }

  function onVerifyDisableButton(fn: () => void, disabledButton: boolean): void {
    if (!disabledButton) {
      fn();
    }
  }

  const disabledIncreaseButton = disabledButtons || setpoint >= maxSetpoint;
  const disabledDecreaseButton = disabledButtons || setpoint <= minSetpoint;
  const disabledPowerButton = power === undefined || !!disabled;

  useEffect(() => {
    if (currentSetpoint && currentSetpoint !== setpoint) {
      setSetpoint(currentSetpoint);
    }
  }, [currentSetpoint]);

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <Skeleton width="36px" height="36px" circle />
        <SetpointControllerContent>
          <SetpointVisualizer
            currentSetpoint={undefined}
            limits={limits}
            disabled
          />
          <SetpointLabel color={colors.Grey400} hasDecimal={false}>
            -
          </SetpointLabel>
          <Skeleton width="40px" height="40px" circle style={{ marginTop: '128px' }} />
        </SetpointControllerContent>
        <Skeleton width="36px" height="36px" circle />
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', alignItems: 'center' }}>
      <SetpointButton
        data-testid="decrease-button"
        onClick={() => onVerifyDisableButton(onClickDecreaseSetpoint, disabledDecreaseButton)}
        type="button"
        {...disabledDecreaseButton && disabledStyleRoundedButton}
      >
        <MinusRCIcon />
      </SetpointButton>
      <SetpointControllerContent>
        <SetpointVisualizer
          currentSetpoint={power && !isSetpointOutRange('min') && !isSetpointOutRange('max') ? setpoint : undefined}
          limits={limits}
          disabled={disabled}
        />
        <SetpointLabel color={disabled ? colors.Grey400 : colors.Blue700} hasDecimal={(power && setpoint) ? (setpoint % 1 !== 0) : false}>
          {(power && setpoint) ? `${setpoint}ºC` : '-'}
        </SetpointLabel>
        <PowerButton
          data-testid="power-button"
          type="button"
          onClick={() => onVerifyDisableButton(onClickPower, disabledPowerButton)}
          off={!!power}
          {...disabledPowerButton && disabledStyleRoundedButton}
        >
          <PowerRCIcon />
        </PowerButton>
      </SetpointControllerContent>
      <SetpointButton
        data-testid="increase-button"
        onClick={() => onVerifyDisableButton(onClickIncreaseSetpoint, disabledIncreaseButton)}
        type="button"
        {...disabledIncreaseButton && disabledStyleRoundedButton}
      >
        <PlusRCIcon />
      </SetpointButton>
    </div>
  );
}
